import * as gottiCore from '@gotti/core';
import {
  HOT_CODE_SYSTEM,
  HOT_CODE_REQUEST,
  hotReloadSystemsFromCode,
  type HotCodeRequest,
  type HotCodeResult,
  type ReceivedMessage,
} from '@gotti/core';
import { ClientSystem } from './IClientSystem.js';
import * as gottiClient from './index.js';

export type HotCodeSystemOptions = {
  /**
   * Extra modules a hot-loaded bundle may `require`/import, by specifier — your
   * own shared packages, when the bundle leaves them external. `@gotti/client`
   * and `@gotti/core` always resolve to the instances this client runs, so a
   * reloaded system extends the same `ClientSystem()`/`System` base as the
   * running ones.
   */
  modules?: Record<string, unknown>;
};

/**
 * The client half of server-driven hot reload. Add it to a client game file's
 * systems (typically a development build) and the area servers this client is
 * in can hot-swap its client systems: `AreaServer.reloadClientSystems` (and the
 * Starter's `reload/area/client*` routes) send a `HotCodeRequest` here; this
 * system evaluates the code, applies its systems to the client process
 * (`applySystemModule` — restart running ones, start named ones, each rolled
 * back on its own if it throws) or removes named systems, and answers with a
 * `HotCodeResult`.
 *
 * A client WITHOUT this system does not run server-sent code: the server's
 * request is answered with an error and reported as such.
 */
export class HotCodeSystem extends ClientSystem() {
  private readonly modules: Record<string, unknown>;
  /** Requests apply one at a time, in arrival order. */
  private queue: Promise<unknown> = Promise.resolve();

  constructor(options: HotCodeSystemOptions = {}) {
    super(HOT_CODE_SYSTEM);
    this.modules = { '@gotti/client': gottiClient, '@gotti/core': gottiCore, ...(options.modules ?? {}) };
  }

  onAreaRequest(_areaId: string | number, request: ReceivedMessage): Promise<HotCodeResult> {
    if (request.type !== HOT_CODE_REQUEST) {
      return Promise.reject(new Error(`${HOT_CODE_SYSTEM} handles only '${HOT_CODE_REQUEST}' requests, got '${String(request.type)}'`));
    }
    const run = this.queue.then(() => this.apply(request.data as HotCodeRequest));
    this.queue = run.catch(() => undefined);
    return run;
  }

  /** Apply one request to this client's process (also callable directly). */
  async apply(req: HotCodeRequest): Promise<HotCodeResult> {
    const process = this.process as unknown as gottiCore.Process;
    if (!req || typeof req.reloadId !== 'string') throw new Error('malformed hot-code request: reloadId missing');
    if (req.op === 'remove') {
      const result: HotCodeResult = { reloadId: req.reloadId, applied: [], started: [], skipped: [], errors: [], removed: [] };
      for (const name of req.systemNames ?? []) {
        const key = String(name);
        if (key === HOT_CODE_SYSTEM) { result.errors.push('the hot-code receiver cannot remove itself'); continue; }
        if (!(key in process.systemLookup)) { result.errors.push(`system '${key}' is not running`); continue; }
        try {
          process.removeSystem(key);
          if (key in process.systemLookup) result.errors.push(`system '${key}' is still owned by another file`);
          else result.removed!.push(key);
        } catch (e) {
          result.errors.push(`${key}: ${e instanceof Error ? e.message : String(e)}`);
        }
      }
      return result;
    }
    if (req.op !== 'apply' || typeof req.code !== 'string') throw new Error(`malformed hot-code request: op '${String(req.op)}' needs code`);
    const applied = await hotReloadSystemsFromCode(process, req.code, {
      filename: req.filename ?? `gotti-hot/${req.reloadId}.js`,
      requireResolver: (id) => this.modules[id],
      logger: () => {},
      // The receiver never hot-swaps itself mid-request (a bundle may re-export it).
      exclude: [HOT_CODE_SYSTEM],
      ...(req.systemNames ? { systemNames: req.systemNames } : {}),
      ...(req.mode ? { mode: req.mode } : {}),
    });
    return { reloadId: req.reloadId, ...applied };
  }
}
